import { useState } from "react";
import api, { fmtMoney, fmtErr, photoSrc } from "../api";
import { PhotoPicker } from "./PhotoPicker";
import { toast } from "sonner";
import { X, Receipt } from "@phosphor-icons/react";

export const ExpensePanel = ({ visitId, expenses = [], receiptRequired = false, canRemove = false, onDone, testid = "expense" }) => {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [amount, setAmount] = useState("");
  const [receipt, setReceipt] = useState([]);

  const add = async () => {
    try {
      await api.post(`/visits/${visitId}/expenses`, { description: desc.trim(), amount: Number(amount || 0), receipt: receipt[0] || null });
      toast.success("Expense added — it will appear on the invoice");
      setOpen(false); setDesc(""); setAmount(""); setReceipt([]);
      onDone();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remove = async (eid) => {
    try { await api.delete(`/visits/${visitId}/expenses/${eid}`); toast.success("Expense removed"); onDone(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <section data-testid={`${testid}-section`}>
      <p className="label-caps mb-2">Reimbursable expenses</p>
      {expenses.length > 0 && (
        <div className="card-flat mb-2 divide-y divide-border" data-testid={`${testid}-list`}>
          {expenses.map((e) => (
            <div key={e.id} className="flex items-center justify-between gap-3 p-4" data-testid={`${testid}-row-${e.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold">{e.description}</p>
                <p className="text-xs text-muted-foreground">{fmtMoney(e.amount)} · {e.added_by}</p>
              </div>
              {e.receipt && (
                <a href={photoSrc(e.receipt)} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-receipt-${e.id}`}>
                  <img src={photoSrc(e.receipt)} alt="receipt" className="h-12 w-12 rounded-md object-cover" />
                </a>
              )}
              {canRemove && (
                <button data-testid={`${testid}-del-${e.id}`} onClick={() => remove(e.id)} className="text-destructive"><X size={18} weight="bold" /></button>
              )}
            </div>
          ))}
        </div>
      )}
      {!open ? (
        <button data-testid={`${testid}-add-btn`} onClick={() => setOpen(true)}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-md border border-dashed border-border text-sm font-bold text-muted-foreground">
          <Receipt size={16} weight="bold" /> Add expense (receipt)
        </button>
      ) : (
        <div className="card-flat space-y-2 p-4" data-testid={`${testid}-form`}>
          <p className="text-xs text-muted-foreground">
            Out-of-pocket cost the owner reimburses (e.g. transfer-station drop-off). It lands on the invoice as its own line{receiptRequired ? " — a receipt photo is required" : ", with the receipt photo when you add one"}.
          </p>
          <input data-testid={`${testid}-desc`} className="field-input w-full" placeholder="Description (e.g. garbage drop-off — transfer station)" value={desc} onChange={(e) => setDesc(e.target.value)} />
          <input data-testid={`${testid}-amount`} type="number" className="field-input w-full" placeholder="Amount $" value={amount} onChange={(e) => setAmount(e.target.value)} />
          <PhotoPicker photos={receipt} setPhotos={setReceipt} min={0} testid={`${testid}-receipt-picker`} />
          <div className="grid grid-cols-2 gap-2">
            <button data-testid={`${testid}-back`} onClick={() => setOpen(false)} className="h-12 rounded-md border border-border text-sm font-bold">Back</button>
            <button data-testid={`${testid}-save`} onClick={add} disabled={!desc.trim() || !(Number(amount) > 0) || (receiptRequired && receipt.length === 0)}
              className="h-12 rounded-md bg-primary text-sm font-bold text-primary-foreground disabled:opacity-40">Add expense</button>
          </div>
        </div>
      )}
    </section>
  );
};
